<template>
  <div v-if="show" class="modal d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ translate('payment.title', 'Purchase Credits') }}</h5>
          <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
        </div>
        
        <!-- Loading data message -->
        <div v-if="isLoadingData" class="modal-body text-center">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">{{ translate('payment.loading', 'Loading...') }}</span>
          </div>
          <p class="mt-2">{{ translate('payment.loadingData', 'Loading payment data...') }}</p>
        </div>
        
        <div v-else class="modal-body">
          <div v-if="error" class="alert alert-danger">{{ error }}</div>
          
          <div class="mb-4">
            <h5>{{ translate('payment.credit_package', 'Credits Package') }}</h5>
            
            <div class="d-flex justify-content-between align-items-center bg-light p-3 mb-3 rounded">
              <div>
                <strong>{{ creditsAmount }} {{ translate('payment.credits', 'Credits') }}</strong>
              </div>
              <div class="text-right">
                <strong v-if="isPlanInUSD">{{ usdAmount }} USD</strong>
                <strong v-else>{{ copAmount }} COP</strong>
              </div>
            </div>
          </div>
          
          <h6>{{ translate('payment.customer_info', 'Customer Information') }}</h6>
          
          <div class="row mb-3">
            <div class="col">
              <label class="form-label">{{ translate('payment.email', 'Email') }}*</label>
              <input 
                type="email" 
                class="form-control" 
                v-model="customerInfo.email" 
                :placeholder="translate('payment.email', 'Email')" 
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-success" style="background-color: #5bc0de; border-color: #5bc0de;" @click="close">
            Cancelar
          </button>
          <button type="button" class="btn btn-primary" style="background-color: #106e91; border-color: #106e91;"
          :disabled="isProcessing || isLoadingData || !copAmount || !usdAmount || !creditsAmount" 
          @click="processPayment">
            <span v-if="isProcessing || isLoadingData" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              <span v-if="isLoadingData">{{ translate('payment.loading', 'Loading...') }}</span>
              <span v-else-if="isProcessing">{{ translate('payment.processing', 'Processing...') }}</span>
              <span v-else>{{ translate('payment.pay_now', 'Pay Now') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { getFirestore, collection, doc, getDoc } from 'firebase/firestore'

export default {
  name: 'EpaycoPaymentModal',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
  },
  
  emits: ['update:show', 'payment-success', 'payment-error'],
  
  setup(props, { emit }) {
    const store = useStore()
    
    // Customer information
    const customerInfo = ref({
      email: '',
      docType: 'CC', // Default document type 
      docNumber: '11111111', // Default document number
      name: 'Cliente',
      last_name: 'Paw Pulse',
      address: 'Dirección predeterminada',
      cell_phone: '3001234567',
    })
    
    // Processing state and errors
    const isProcessing = ref(false)
    const error = ref(null)
    
    // Dynamic values from Firebase that must be loaded
    const copAmount = ref(null)
    const usdAmount = ref(null)
    const creditsAmount = ref(null)
    
    // Loading data state
    const isLoadingData = ref(true)
    
    // Determine user language and corresponding plan
    const currentLanguage = computed(() => store.state.language || 'en')
    const isPlanInUSD = computed(() => currentLanguage.value !== 'es')
    
    // Function to load the ePayco script
    const loadEpaycoScript = () => {
      return new Promise((resolve, reject) => {
        try {
          // Check if the script is already loaded
          if (window.ePayco) {

            resolve(window.ePayco)
            return
          }
          
          // Create the script if it doesn't exist
          if (!document.querySelector('script[src="https://checkout.epayco.co/checkout.js"]')) {
            const script = document.createElement('script')
            script.src = 'https://checkout.epayco.co/checkout.js'
            script.type = 'text/javascript'
            
            script.onload = () => {
              resolve(window.ePayco)
            }
            
            script.onerror = () => {
              reject(new Error('Error to load ePayco'))
            }
            
            document.head.appendChild(script)
          } else {
            resolve(window.ePayco)
          }
        } catch (err) {
          reject(err)
        }
      })
    }
    
    // Function to load dynamic values from Firebase
    const loadPriceFromFirebase = async () => {
      isLoadingData.value = true
      error.value = null
      
      try {
        const db = getFirestore()
        let dataLoadedSuccessfully = true
      
        const parametersRef = collection(db, 'parameters');
        
        // Get COP price document
        const copAmountDoc = doc(parametersRef, 'copAmount');
        const copAmountSnapshot = await getDoc(copAmountDoc);
        
        // Get USD price document
        const usdAmountDoc = doc(parametersRef, 'usdAmount');
        const usdAmountSnapshot = await getDoc(usdAmountDoc);
        
        // Get credits amount document
        const creditsDoc = doc(parametersRef, 'amountCredits');
        const creditsSnapshot = await getDoc(creditsDoc);
        
        // Check if documents exist and extract data
        if (!copAmountSnapshot.exists() || !usdAmountSnapshot.exists() || !creditsSnapshot.exists()) {
          dataLoadedSuccessfully = false;
        } else {
          // Extract COP amount
          if (copAmountSnapshot.data() && copAmountSnapshot.data().value) {
            copAmount.value = copAmountSnapshot.data().value.toString();
          } else {
            dataLoadedSuccessfully = false;
          }
          
          // Extract USD amount
          if (usdAmountSnapshot.data() && usdAmountSnapshot.data().value) {
            usdAmount.value = usdAmountSnapshot.data().value.toString();
          } else {
            dataLoadedSuccessfully = false;
          }
          
          // Extract credits amount
          if (creditsSnapshot.data() && creditsSnapshot.data().value) {
            creditsAmount.value = creditsSnapshot.data().value.toString();
          } else {
            dataLoadedSuccessfully = false;
          }
        }
        
        if (!dataLoadedSuccessfully) {
          error.value = translate('payment.dataError', 'Error loading payment data: Missing or incorrect data')
        }
        
        isLoadingData.value = false
        return dataLoadedSuccessfully
        
      } catch (err) {
        error.value = translate('payment.loadError', 'Error loading payment data: ') + err.message
        isLoadingData.value = false
        return false
      }
    }
    
    // Method to translate text
    const translate = (key, fallback) => {
      return store.getters.translate ? store.getters.translate(key, fallback) : fallback
    }
    
    // Method to close the modal
    const close = () => {
      emit('update:show', false)
    }
    
    // Validate email format
    const validateEmail = (email) => {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return regex.test(email)
    }
    
    // Load ePayco script when the component is mounted
    onMounted(() => {
      loadEpaycoScript()
      
      // Load prices from Firebase
      loadPriceFromFirebase()
      // Pre-fill customer information
      if (store.state.user) {
        const user = store.state.user
        customerInfo.value.email = user.email || ''
      }
    })
    
    // Method to process payment
    const processPayment = async () => {
      try {
        isProcessing.value = true
        error.value = null
        
        // Minimum validation (we only need email)
        if (!customerInfo.value.email || !validateEmail(customerInfo.value.email)) {
          error.value = translate('payment.invalidEmail', 'Please enter a valid email address')
          isProcessing.value = false
          return
        }
        
        // Verify if prices and credits data are loaded
        if (isLoadingData.value) {
          error.value = translate('payment.loadingData', 'Loading payment data, please wait...')
          isProcessing.value = false
          return
        }
        
        // Verify that all dynamic values have been loaded correctly
        if (!copAmount.value || !usdAmount.value || !creditsAmount.value) {
          // Try to load the data again
          const dataLoaded = await loadPriceFromFirebase()
          if (!dataLoaded) {
            error.value = translate('payment.dataNotAvailable', 'No charge data to load')
            isProcessing.value = false
            return
          }
        }
        
        // Verify if ePayco script is loaded
        if (!window.ePayco) {
          try {
            await loadEpaycoScript()
          } catch (err) {
            error.value = translate('payment.notLoaded', 'The payment system could not be loaded. Please refresh the page and try again.')
            isProcessing.value = false
            return
          }
          
          if (!window.ePayco) {
            error.value = translate('payment.notLoaded', 'The payment system is not available. Please refresh the page.')
            isProcessing.value = false
            return
          }
        }
        
        // Configure checkout data
        const handler = window.ePayco.checkout.configure({
          key: '7e66c11f058a4acbac8fcf36f7480fbb',
          test: false  
        })
        
        // Open checkout
        handler.open({
          // Purchase information
          name: translate('payment.creditsPackage', 'Credits Package'),
          description: translate('payment.creditsDesc', `${creditsAmount.value} Credits for Paw Pulse`),
          currency: isPlanInUSD.value ? 'USD' : 'COP',
          amount: isPlanInUSD.value ? usdAmount.value : copAmount.value,
          tax_base: '0',
          tax: '0',
          country: 'co',
          lang: isPlanInUSD.value ? 'en' : 'es',
          
          // Customer information
          
          doc_type: customerInfo.value.docType,
          doc_number: customerInfo.value.docNumber,
          name_billing: customerInfo.value.name,
          lastname_billing: customerInfo.value.last_name,
          email_billing: customerInfo.value.email,
          address_billing: customerInfo.value.address,
          cell_phone: customerInfo.value.cell_phone,
          
          // Custom fields - the user ID is required for the webhook
          extra1: store.state.user ? store.state.user.uid : '',
      
          // Response URLs - URL absoluta y directa para forzar el retorno
          response: window.location.origin + '/home?payment_success=true', 
          confirmation: 'https://us-central1-technopaws-86c5f.cloudfunctions.net/paymentWebhook',
          // Important: ePayco respects this URL to redirect after the payment
          // The payment_success parameter helps identify that it returns from a payment
          
          // Callback when the user completes the process in the form
          callback: function(data) {
            
            // Independently of the payment result, redirect the user
            // This callback is executed when the user clicks on return from the ePayco checkout
            setTimeout(() => {
              
              if (data && data.success) {
                // Notify that the payment is being processed
                emit('payment-success', {
                  message: translate('payment.processing', 'Payment is being processed'),
                  data
                })
              } else {
                // Show error if there is no success
                error.value = data?.message || translate('payment.genericError', 'Error processing payment')
                isProcessing.value = false
              }
              
              // Close modal and force redirection to dashboard (independently of the result)
              close();
              
              // To ensure that the user returns to the dashboard and does not stay in the receipt
              // (forced redirection with timeout to give time to the event)
              setTimeout(() => {
                window.location.href = window.location.origin + '/home?payment_complete=1';
              }, 300);
            }, 100);
          }
        })
        
        // The checkout opens in another window, so we can mark as not processing after a time
        setTimeout(() => {
          isProcessing.value = false
        }, 3000)
        
      } catch (err) {
        error.value = err.message || translate('payment.genericError', 'Error initializing payment')
        isProcessing.value = false
      }
    }
    
    return {
      customerInfo,
      isProcessing,
      error,
      isPlanInUSD,
      isLoadingData,
      copAmount,
      usdAmount,
      creditsAmount,
      translate,
      close,
      processPayment
    }
  }
}
</script>

<style scoped>
.modal-backdrop {
  opacity: 0.5;
}
</style>
